'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Sparkles } from 'lucide-react';

const FUNNY_LINES = [
  "Looks like this page went for a coffee break and forgot to come back.",
  "We searched every folder on the server. Only found dust bunnies.",
  "This URL is more lost than my keys on a Monday morning.",
  "404: page last seen wearing a red shirt. You know what that means.",
  "Even Google can't find this one. And it finds everything.",
];

export default function NotFound() {
  const line = FUNNY_LINES[Math.floor(Math.random() * FUNNY_LINES.length)];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#fafaff] via-white to-[#f6f3ff] dark:from-[#0b0821] dark:via-[#12102b] dark:to-[#0b0821]">
      {/* Matching the site's hero blobs */}
      <div className="hero-blob hero-blob-1" />
      <div className="hero-blob hero-blob-2" />

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-5rem-200px)] max-w-6xl flex-col items-center justify-center px-6 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#6C63FF]/20 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#6C63FF] shadow-sm backdrop-blur-md dark:bg-white/5 dark:border-white/10 dark:text-[#A78BFA]"
        >
          <Sparkles size={14} />
          Page Not Found
        </motion.div>

        <motion.div
          className="mt-8 relative"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [-2, 2, -2] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <LostExplorer />
          </motion.div>
        </motion.div>

        <motion.h1
          className="mt-6 font-extrabold leading-none tracking-tight text-slate-900 dark:text-white"
          style={{ fontSize: 'clamp(4.5rem, 15vw, 10rem)' }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <span className="gradient-text">404</span>
        </motion.h1>

        <motion.h2
          className="mt-2 text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white/90"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          Oops — wrong turn!
        </motion.h2>

        <motion.p
          className="mt-4 max-w-xl text-base sm:text-lg text-slate-600 dark:text-white/70 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {line}
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Link
            href="/"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#6C63FF] to-[#FF6584] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#6C63FF]/25 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#FF6584]/30"
          >
            <Home className="h-4 w-4 transition-transform group-hover:-rotate-6" />
            Take Me Home
          </Link>
          <button
            onClick={() => window.history.back()}
            className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 px-7 py-3.5 text-sm font-semibold text-slate-700 shadow-sm backdrop-blur transition hover:border-[#6C63FF] hover:text-[#6C63FF] hover:shadow-md dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:border-[#A78BFA] dark:hover:text-[#A78BFA]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Go Back
          </button>
        </motion.div>

        <motion.div
          className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500 dark:text-white/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <span>Try one of these instead:</span>
          <Link href="/about" className="font-medium text-[#6C63FF] hover:underline dark:text-[#A78BFA]">About</Link>
          <Link href="/projects" className="font-medium text-[#6C63FF] hover:underline dark:text-[#A78BFA]">Projects</Link>
          <Link href="/blog" className="font-medium text-[#6C63FF] hover:underline dark:text-[#A78BFA]">Blog</Link>
          <Link href="/contact" className="font-medium text-[#6C63FF] hover:underline dark:text-[#A78BFA]">Contact</Link>
        </motion.div>
      </div>
    </section>
  );
}

function LostExplorer() {
  return (
    <svg
      viewBox="0 0 320 260"
      width="260"
      height="210"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-[0_18px_40px_rgba(108,99,255,0.25)]"
    >
      <defs>
        <linearGradient id="nf-map-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#F0EEFF" />
        </linearGradient>
        <linearGradient id="nf-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6C63FF" />
          <stop offset="100%" stopColor="#5A50F0" />
        </linearGradient>
        <linearGradient id="nf-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FF6584" />
          <stop offset="100%" stopColor="#FF84A2" />
        </linearGradient>
        <radialGradient id="nf-planet" cx="0.35" cy="0.3" r="0.9">
          <stop offset="0%" stopColor="#FFB86B" />
          <stop offset="100%" stopColor="#FF6584" />
        </radialGradient>
      </defs>

      {/* Floating dots/confetti */}
      {[
        { cx: 30, cy: 40, r: 3, fill: '#FF6584', dur: 3 },
        { cx: 290, cy: 60, r: 4, fill: '#6C63FF', dur: 3.5 },
        { cx: 40, cy: 200, r: 3, fill: '#FFB86B', dur: 2.8 },
        { cx: 280, cy: 210, r: 3.5, fill: '#A78BFA', dur: 3.2 },
        { cx: 60, cy: 120, r: 2, fill: '#FF6584', dur: 2.5 },
        { cx: 270, cy: 140, r: 2.5, fill: '#6C63FF', dur: 3.8 },
      ].map((d, i) => (
        <motion.circle
          key={i}
          cx={d.cx}
          cy={d.cy}
          r={d.r}
          fill={d.fill}
          animate={{ y: [0, -8, 0], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: d.dur, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }}
        />
      ))}

      {/* Planet bottom-right */}
      <g>
        <circle cx="258" cy="225" r="16" fill="url(#nf-planet)" />
        <ellipse cx="258" cy="225" rx="24" ry="6" fill="none" stroke="#FF6584" strokeOpacity="0.5" strokeWidth="1.5" transform="rotate(-18 258 225)" />
      </g>

      {/* Little character holding a map - "lost" theme */}
      <g transform="translate(110 60)">
        {/* Shadow */}
        <ellipse cx="50" cy="170" rx="44" ry="6" fill="#6C63FF" opacity="0.12" />

        {/* Legs */}
        <rect x="32" y="130" width="14" height="30" rx="5" fill="url(#nf-body)" />
        <rect x="54" y="130" width="14" height="30" rx="5" fill="url(#nf-body)" />
        <rect x="28" y="156" width="20" height="8" rx="3" fill="#1a1040" />
        <rect x="52" y="156" width="20" height="8" rx="3" fill="#1a1040" />

        {/* Body */}
        <rect x="22" y="70" width="56" height="68" rx="16" fill="url(#nf-body)" />

        {/* Map in hands */}
        <motion.g
          style={{ transformOrigin: '50px 105px' }}
          animate={{ rotate: [-4, 4, -4] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        >
          <rect x="12" y="88" width="76" height="46" rx="4" fill="url(#nf-map-bg)" stroke="#6C63FF" strokeWidth="1.5" />
          {/* Map folds */}
          <line x1="37" y1="88" x2="37" y2="134" stroke="#6C63FF" strokeOpacity="0.3" strokeWidth="1" />
          <line x1="62" y1="88" x2="62" y2="134" stroke="#6C63FF" strokeOpacity="0.3" strokeWidth="1" />
          {/* Dashed route */}
          <path d="M20 100 Q 40 110, 50 115 T 80 128" fill="none" stroke="#FF6584" strokeWidth="2" strokeDasharray="3 3" strokeLinecap="round" />
          {/* Question mark on map */}
          <text x="50" y="116" textAnchor="middle" fontFamily="sans-serif" fontWeight="800" fontSize="16" fill="#FF6584">?</text>
        </motion.g>

        {/* Head */}
        <circle cx="50" cy="48" r="30" fill="#FFE3C7" />

        {/* Hat */}
        <path d="M20 44 Q 50 18, 80 44 L 80 48 L 20 48 Z" fill="url(#nf-accent)" />
        <rect x="18" y="44" width="64" height="5" rx="2" fill="#FF6584" />
        <circle cx="77" cy="26" r="5" fill="#FFB86B" />

        {/* Face */}
        <motion.g
          animate={{ scaleY: [1, 0.1, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', times: [0, 0.5, 1] }}
          style={{ transformOrigin: '50px 52px' }}
        >
          <circle cx="40" cy="52" r="2.5" fill="#1a1040" />
          <circle cx="60" cy="52" r="2.5" fill="#1a1040" />
        </motion.g>
        {/* Confused squiggle mouth */}
        <path d="M42 64 Q 46 61, 50 64 T 58 64" fill="none" stroke="#1a1040" strokeWidth="2" strokeLinecap="round" />
        {/* Cheeks */}
        <circle cx="36" cy="58" r="3" fill="#FF6584" opacity="0.4" />
        <circle cx="64" cy="58" r="3" fill="#FF6584" opacity="0.4" />

        {/* Thought bubble with ? */}
        <motion.g
          animate={{ y: [0, -6, 0], opacity: [0.9, 1, 0.9] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <circle cx="100" cy="20" r="14" fill="white" stroke="#6C63FF" strokeWidth="2" />
          <circle cx="88" cy="32" r="4" fill="white" stroke="#6C63FF" strokeWidth="2" />
          <circle cx="82" cy="40" r="2" fill="white" stroke="#6C63FF" strokeWidth="2" />
          <text x="100" y="26" textAnchor="middle" fontFamily="sans-serif" fontWeight="800" fontSize="16" fill="#6C63FF">?</text>
        </motion.g>
      </g>
    </svg>
  );
}
